interface PerformanceMetrics {
  subscriptionCount: number;
  dataTransferSize: number;
  updateFrequency: number;
  responseTime: number;
  timestamp: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics[] = [];
  private startTime: number = Date.now();
  private updateCount: number = 0;
  private totalDataTransfer: number = 0;

  constructor() {
    console.log('Performance Monitor initialized - tracking real-time metrics');
  }

  // Track subscription performance
  trackSubscription(operation: string, dataSize: number, responseTime: number) {
    this.updateCount++;
    this.totalDataTransfer += dataSize;

    const metric: PerformanceMetrics = {
      subscriptionCount: this.getCurrentSubscriptionCount(),
      dataTransferSize: dataSize,
      updateFrequency: this.updateCount / ((Date.now() - this.startTime) / 1000), // updates per second
      responseTime,
      timestamp: Date.now()
    };

    this.metrics.push(metric);

    // Keep only last 100 metrics
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }

    console.log(`Performance: ${operation}`, {
      dataSize: `${dataSize} bytes`,
      responseTime: `${responseTime}ms`,
      totalUpdates: this.updateCount,
      avgFrequency: `${metric.updateFrequency.toFixed(2)}/s`
    });
  }



  private getCurrentSubscriptionCount(): number {
    // This would be integrated with the selective realtime manager
    // For now, return a reasonable estimate based on active metrics
    return this.metrics.length > 0 ? Math.min(this.metrics.length, 10) : 0;
  }

  // Get performance summary
  getPerformanceSummary() {
    if (this.metrics.length === 0) {
      return {
        totalUpdates: 0,
        averageResponseTime: 0,
        averageDataTransfer: 0,
        averageUpdateFrequency: 0,
        totalDataTransfer: 0,
        uptime: 0
      };
    }

    const avgResponseTime = this.metrics.reduce((sum, m) => sum + m.responseTime, 0) / this.metrics.length;
    const avgDataTransfer = this.metrics.reduce((sum, m) => sum + m.dataTransferSize, 0) / this.metrics.length;
    const avgUpdateFrequency = this.metrics.reduce((sum, m) => sum + m.updateFrequency, 0) / this.metrics.length;

    return {
      totalUpdates: this.updateCount,
      averageResponseTime: Math.round(avgResponseTime),
      averageDataTransfer: Math.round(avgDataTransfer),
      averageUpdateFrequency: Math.round(avgUpdateFrequency * 100) / 100,
      totalDataTransfer: this.totalDataTransfer,
      uptime: Math.round((Date.now() - this.startTime) / 1000)
    };
  }

  // Get recent performance trends
  getRecentTrends(minutes: number = 5) {
    const cutoff = Date.now() - (minutes * 60 * 1000);
    const recentMetrics = this.metrics.filter(m => m.timestamp > cutoff);

    if (recentMetrics.length === 0) {
      return {
        recentUpdates: 0,
        recentAverageResponseTime: 0,
        recentAverageDataTransfer: 0
      };
    }

    const avgResponseTime = recentMetrics.reduce((sum, m) => sum + m.responseTime, 0) / recentMetrics.length;
    const avgDataTransfer = recentMetrics.reduce((sum, m) => sum + m.dataTransferSize, 0) / recentMetrics.length;

    return {
      recentUpdates: recentMetrics.length,
      recentAverageResponseTime: Math.round(avgResponseTime),
      recentAverageDataTransfer: Math.round(avgDataTransfer)
    };
  }

  // Reset metrics
  reset() {
    this.metrics = [];
    this.startTime = Date.now();
    this.updateCount = 0;
    this.totalDataTransfer = 0;
  }
}

export const performanceMonitor = new PerformanceMonitor(); 